import React, { useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, Alert } from "react-bootstrap";
import "./CameraCapture.css";

const CameraCapture = ({ mode, facingMode, onCapture, onFileSelect, allowFileUpload }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [, setCameraActive] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const resolvedFacingMode = facingMode || (mode === "selfie" ? "user" : "environment");

  const startCamera = useCallback(async () => {
    setError(null);
    setPermissionDenied(false);

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: resolvedFacingMode },
      });
      setStream(mediaStream);
      setCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      if (
        window.location.protocol !== "https:" &&
        window.location.hostname !== "localhost"
      ) {
        setError("https_required");
        return;
      }
      if (
        err.name === "NotAllowedError" ||
        err.name === "PermissionDeniedError"
      ) {
        setPermissionDenied(true);
        setError("camera_permission_denied");
        return;
      }
      if (
        err.name === "NotFoundError" ||
        err.name === "DevicesNotFoundError"
      ) {
        setError("no_camera_found");
        return;
      }
      setError("no_camera_found");
    }
  }, [resolvedFacingMode]);

  useEffect(() => {
    startCamera();
    return () => {
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const capture = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    const MAX_DIM = 1280;
    const MAX_BYTES = 5 * 1024 * 1024;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const scale = Math.min(1, MAX_DIM / Math.max(vw, vh));
    canvas.width = Math.round(vw * scale);
    canvas.height = Math.round(vh * scale);

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const tryBlob = (quality) => {
      canvas.toBlob(
        (blob) => {
          if (blob.size > MAX_BYTES && quality > 0.4) {
            tryBlob(quality - 0.1);
            return;
          }
          const previewUrl = URL.createObjectURL(blob);
          setPreview(previewUrl);
          setCameraActive(false);
          if (stream) {
            stream.getTracks().forEach((t) => t.stop());
          }
          if (onCapture) {
            onCapture(blob, {
              captureSource: "camera",
              capturedAt: new Date().toISOString(),
            });
          }
        },
        "image/jpeg",
        quality
      );
    };

    tryBlob(0.85);
  }, [stream, onCapture]);

  const retake = useCallback(() => {
    if (preview) {
      URL.revokeObjectURL(preview);
      setPreview(null);
    }
    startCamera();
  }, [preview, startCamera]);

  const compressImage = useCallback((file, callback) => {
    const MAX_DIM = 1280;
    const MAX_BYTES = 5 * 1024 * 1024;

    if (!file.type.startsWith("image/") || file.type === "image/webp") {
      callback(file);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);

      const cvs = document.createElement("canvas");
      cvs.width = w;
      cvs.height = h;
      cvs.getContext("2d").drawImage(img, 0, 0, w, h);

      const tryBlob = (quality) => {
        cvs.toBlob(
          (blob) => {
            if (blob.size > MAX_BYTES && quality > 0.4) {
              tryBlob(quality - 0.1);
              return;
            }
            const compressed = new File([blob], file.name, { type: "image/jpeg" });
            callback(compressed);
          },
          "image/jpeg",
          quality
        );
      };

      if (file.size <= MAX_BYTES && scale === 1) {
        callback(file);
      } else {
        tryBlob(0.85);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      callback(file);
    };
    img.src = url;
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onFileSelect) {
      compressImage(file, (processed) => {
        onFileSelect(processed, {
          captureSource: "file",
          capturedAt: new Date().toISOString(),
        });
      });
    }
  };

  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (preview) {
    return (
      <div className="camera-container">
        <img src={preview} alt="Captured" className="camera-preview" />
        <div className="camera-controls">
          <Button variant="outline-secondary" onClick={retake}>
            {t("retake")}
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="camera-error">
        <Alert variant="warning">{t(error)}</Alert>
        {permissionDenied && (
          <Button variant="primary" className="mt-2" onClick={startCamera}>
            {t("retry_camera")}
          </Button>
        )}
        {mode === "selfie" && (
          <p className="mt-3 text-muted">{t("try_from_phone")}</p>
        )}
        {mode === "document" && allowFileUpload && (
          <div className="mt-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={handleFileChange}
              className="form-control"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="camera-container">
      <div style={{ position: "relative" }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`camera-video ${mode === "selfie" ? "camera-selfie-mirror" : ""}`}
        />
        <div className="camera-guide-frame" />
      </div>
      <canvas ref={canvasRef} style={{ display: "none" }} />
      <div className="camera-controls">
        <button
          type="button"
          className="camera-capture-btn"
          onClick={capture}
          aria-label={t("capture")}
        />
      </div>
      {mode === "document" && allowFileUpload && (
        <div className="mt-3 text-center">
          <small className="text-muted d-block mb-2">{t("upload_or_capture")}</small>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={handleFileChange}
            className="form-control"
          />
        </div>
      )}
    </div>
  );
};

export default CameraCapture;
